import { type DragEvent, useCallback, useEffect, useRef } from "react";

type FileDnDWrapperProps = {
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  onDragLeave?: (e: DragEvent<HTMLDivElement>) => void;
  onError?: (message: string) => void;
  children: React.ReactNode;
  containerStyle?: React.CSSProperties;
  disabled?: boolean;
  handleSelectedFiles: (e: DragEvent<HTMLDivElement>) => void;
};

export const FileDnDWrapper = (props: FileDnDWrapperProps) => {
  const {
    onDragOver = () => {},
    onDragLeave = () => {},
    children,
    containerStyle,
    disabled,
    handleSelectedFiles,
  } = props;
  const leaveTimeout = useRef<number | null>(null);
  const dropRef = useRef(null);

  const handleDragEvent = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback(
    (e) => {
      if (disabled) return;
      handleDragEvent(e);
      onDragOver(e);
    },
    [onDragOver, disabled, handleDragEvent],
  );

  const handleDragOver = useCallback(
    (e) => {
      if (leaveTimeout.current) {
        clearTimeout(leaveTimeout.current);
        leaveTimeout.current = null;
      }
      if (disabled) return;
      handleDragEvent(e);
      onDragOver(e);
    },
    [onDragOver, disabled, handleDragEvent],
  );

  const handleDragLeave = useCallback(
    (e) => {
      if (disabled) return;
      handleDragEvent(e);
      if (leaveTimeout.current) {
        clearTimeout(leaveTimeout.current);
      }
      leaveTimeout.current = setTimeout(() => {
        onDragLeave(e);
      }, 150);
    },
    [onDragLeave, disabled, handleDragEvent],
  );

  const handleDrop = useCallback(
    (e) => {
      if (disabled) return;
      handleDragEvent(e);
      handleSelectedFiles(e);
      onDragLeave(e);
    },
    [disabled, handleSelectedFiles, onDragLeave, handleDragEvent],
  );

  useEffect(() => {
    const dropContainer = dropRef.current;
    dropContainer.addEventListener("dragenter", handleDragEnter);
    dropContainer.addEventListener("dragleave", handleDragLeave);
    dropContainer.addEventListener("dragover", handleDragOver);
    dropContainer.addEventListener("drop", handleDrop);
    return () => {
      dropContainer.removeEventListener("dragenter", handleDragEnter);
      dropContainer.removeEventListener("dragleave", handleDragLeave);
      dropContainer.removeEventListener("dragover", handleDragOver);
      dropContainer.removeEventListener("drop", handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return (
    <div ref={dropRef} style={containerStyle}>
      {children}
    </div>
  );
};
